/**
 * Same centering, two triggers:
 *  - `useCenterMap` - declarative: follows a coordinate as state changes.
 *  - `useRecenterMap` - imperative: returns an action to center now.
 */
import { MutableRefObject, useEffect, useMemo, useRef } from 'react';
import { useMediaQuery, useTheme } from '@mui/material';
import { useLocation } from 'react-router-dom';
import { useEventCallback } from 'usehooks-ts';
import { MapRef, useMap, useMapInstance } from 'shared/map-exports';
import { LatLng } from 'shared/types';
import { hasValidCoordinates } from 'shared/utils';
import { useSearchDrawerState } from 'state/useSearchDrawerState';
import { useMapEvent } from 'features/mapping/hooks/useMapEvent';
import { isUserInitiatedMove } from 'features/mapping/mapping.utils';
import { useKeyboardJustClosed } from 'hooks/useKeyboardJustClosed';
import {
  CenterMapOptions,
  centerMapOnLatLng,
  getCenterOffset,
  getTargetZoom,
} from './useCenterMap.utils';

export type UseCenterMapProps = CenterMapOptions & {
  latLng: LatLng | null;
};

type RecenterMapOptions = CenterMapOptions & {
  /** Defaults to the map from context; pass explicitly from outside that tree. */
  map?: MapRef;
};

type RecenterMap = (latLng: LatLng, options: RecenterMapOptions) => void;

type GetCenterOffset = (
  input: Pick<CenterMapOptions, 'drawerIsOpen' | 'defaultSnapPointPct'>,
) => [number, number];

/** Getter, not a value: the offset depends on layout at the moment we center. */
const useCenterOffset = (): GetCenterOffset => {
  const theme = useTheme();
  const isLargeMediaQuery = useMediaQuery(theme.breakpoints.up('tablet'));
  const { open: isSearchDrawerOpen } = useSearchDrawerState();

  return useEventCallback(({ drawerIsOpen = false, defaultSnapPointPct }) =>
    getCenterOffset({
      isLargeMediaQuery,
      drawerIsOpen,
      isSearchDrawerOpen,
      defaultSnapPointPct,
    }),
  );
};

/**
 * Tracks whether the user has manually moved the map.
 * Returns a ref that can be reset by the consumer.
 */
const useUserHasMovedMap = (map?: MapRef): MutableRefObject<boolean> => {
  const hasUserMovedRef = useRef(false);

  useMapEvent(
    'movestart',
    (event) => {
      if (isUserInitiatedMove(event)) {
        hasUserMovedRef.current = true;
      }
    },
    map,
    true,
  );

  return hasUserMovedRef;
};

/**
 * Keeps the map on `latLng` as it changes. Yields to a manual pan and to
 * `skipMapCentering` so it never fights the user.
 */
export const useCenterMap = (props: UseCenterMapProps): void => {
  const {
    drawerIsOpen,
    latLng: latLngProp,
    zoomIn,
    defaultSnapPointPct,
    zoom,
  } = props;
  const { current: map } = useMap();
  const getOffset = useCenterOffset();
  const location = useLocation<{
    skipMapCentering?: boolean;
    skipZoom?: boolean;
  }>();
  const skipMapCentering = Boolean(location.state?.skipMapCentering);
  const skipZoom = Boolean(location.state?.skipZoom);
  const hasUserMovedRef = useUserHasMovedMap(map);

  // Stabilize by coordinate value so a fresh object at the same position (e.g.
  // cache-buster refetches, back-navigation) doesn't re-run centering over a
  // user's manual pan.
  const latLng = useMemo<LatLng | null>(() => {
    const coords = {
      lat: latLngProp?.lat ?? null,
      lng: latLngProp?.lng ?? null,
    };
    return hasValidCoordinates(coords) ? coords : null;
  }, [latLngProp?.lat, latLngProp?.lng]);

  useEffect(() => {
    // A new position should re-center, even if the user had panned.
    hasUserMovedRef.current = false;
  }, [latLng, hasUserMovedRef]);

  // `useEventCallback` keeps a stable identity while reading latest values live;
  // the effect below defers it via `setTimeout` (never during render) so the
  // layout has settled before the offset is computed.
  const applyCenter = useEventCallback((): void => {
    if (skipMapCentering || !map || !latLng || hasUserMovedRef.current) return;

    centerMapOnLatLng({
      map,
      latLng,
      zoom: getTargetZoom({ map, zoom, zoomIn, skipZoom }),
      offset: getOffset({ drawerIsOpen, defaultSnapPointPct }),
    });
  });

  // Re-center when the position changes, or when the map first becomes ready
  // (undefined -> instance, e.g. a deep-linked selection on cold load).
  useEffect(() => {
    const timeoutId = setTimeout(applyCenter, 0);
    return () => clearTimeout(timeoutId);
  }, [applyCenter, latLng, map]);

  // Re-center after the keyboard hides (search-drawer path): the first easeTo's
  // vertical offset was derived from the keyboard-shrunk window.innerHeight, so
  // re-run it once the viewport is back to full height.
  const hasKeyboardClosed = useKeyboardJustClosed();
  useEffect(() => {
    if (!hasKeyboardClosed || !map) return undefined;
    const timeoutId = setTimeout(applyCenter, 0);
    return () => clearTimeout(timeoutId);
  }, [hasKeyboardClosed, map, applyCenter]);
};

/**
 * Returns a re-center action for user commands ("go to the locked pin"). Prefer
 * `useCenterMap` when state is the trigger; it also waits for the map.
 *
 * The action ignores a manual pan and `skipMapCentering`: those stop automatic
 * centering from fighting the user, not a request the user just made. Omit
 * `zoom`/`zoomIn` to keep the current zoom.
 */
export const useRecenterMap = (): RecenterMap => {
  const mapFromContext = useMapInstance();
  const getOffset = useCenterOffset();

  return useEventCallback((latLng, options) => {
    const {
      map = mapFromContext,
      zoom,
      zoomIn,
      drawerIsOpen,
      defaultSnapPointPct,
    } = options;

    if (!map || !hasValidCoordinates(latLng)) {
      return;
    }

    centerMapOnLatLng({
      map,
      latLng,
      zoom: getTargetZoom({ map, zoom, zoomIn, skipZoom: false }),
      offset: getOffset({ drawerIsOpen, defaultSnapPointPct }),
    });
  });
};
